import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  Users,
  BookOpen,
  Calendar,
  Target,
  Award,
  CheckCircle,
  ArrowRight,
  Star,
  Clock,
  MessageCircle,
  TrendingUp,
  Shield,
  Zap,
  Heart,
  Globe,
  PlayCircle,
  FileText,
  BarChart3,
  UserCheck,
  Check,
  Sparkles,
  Fish
} from 'lucide-react';
import { Button } from 'src/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/components/ui/Card';
import { Badge } from 'src/components/ui/Badge';
import Navbar from 'src/components/Navbar';
import Footer from 'src/components/Footer';
import AnimatedFavicon from 'src/components/AnimatedFavicon';
import { useCountryPricing } from 'src/hooks/useCountryPricing';

// Icon mapping for dynamic icon rendering
const iconMap = {
  Zap,
  Target,
  Award
};

const bloomCatches = [
  { name: 'Remembering', scale: 0.72 },
  { name: 'Understanding', scale: 0.86 },
  { name: 'Applying', scale: 1 },
  { name: 'Analyzing', scale: 1.14 },
  { name: 'Evaluating', scale: 1.28 },
  { name: 'Creating', scale: 1.42 }
];

const studentFeatures = [
  { icon: Calendar, title: 'Flexible Scheduling', description: 'Book sessions around your life, with availability that stays in step with your routine.', features: ['Real-time booking', 'Conflict resolution', 'Automatic reminders'], accent: 'blue' },
  { icon: BarChart3, title: 'Progress Tracking', description: 'See the full picture of your learning through milestones, patterns, and next steps.', features: ['Visual progress maps', 'Performance metrics', 'Goal tracking'], accent: 'violet' },
  { icon: FileText, title: 'Learning Materials', description: 'Keep every useful resource, recording, and practice activity close at hand.', features: ['Digital materials', 'Session recordings', 'Practice exercises'], accent: 'teal' },
  { icon: MessageCircle, title: 'Direct Communication', description: 'Stay connected between sessions, whenever a question is still on your mind.', features: ['In-app messaging', 'Session feedback', 'Quick questions'], accent: 'coral' },
  { icon: Award, title: 'Certified Tutors', description: 'Learn with people chosen for their expertise, care, and proven teaching practice.', features: ['Background verified', 'Subject specialists', 'Continuous training'], accent: 'gold' },
  { icon: Shield, title: 'Secure Platform', description: 'Focus on learning in a space built to protect your information and payments.', features: ['Data encryption', 'Secure payments', 'Privacy controls'], accent: 'indigo' }
];

const tutorBenefits = [
  { icon: Globe, title: 'Global Reach', description: 'Teach students from around the world with our online platform', highlight: 'Unlimited potential' },
  { icon: Clock, title: 'Flexible Schedule', description: 'Set your own availability and work when it suits you best', highlight: 'Work-life balance' },
  { icon: TrendingUp, title: 'Competitive Rates', description: 'Earn competitive hourly rates with transparent payment system', highlight: 'Fair compensation' },
  { icon: UserCheck, title: 'Student Matching', description: 'Get matched with students who need your specific expertise', highlight: 'Perfect fit' },
  { icon: BarChart3, title: 'Performance Insights', description: 'Track your teaching effectiveness with detailed analytics', highlight: 'Continuous improvement' },
  { icon: Heart, title: 'Meaningful Impact', description: 'Help students achieve their academic goals and dreams', highlight: 'Make a difference' }
];

const LearningPond = () => {
  const pondRef = useRef<HTMLElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const pond = pondRef.current;
    if (!pond) return;

    const observer = new IntersectionObserver(([entry]) => setIsPlaying(entry.isIntersecting), {
      threshold: 0.25
    });
    observer.observe(pond);
    return () => observer.disconnect();
  }, []);

  return (
  <figure
    ref={pondRef}
    className={`learning-pond relative isolate mx-auto mb-20 max-w-6xl overflow-hidden rounded-2xl bg-sky-950 shadow-[0_26px_70px_rgba(14,53,84,0.2)] ${isPlaying ? 'is-playing' : ''}`}
    aria-labelledby="learning-pond-title"
  >
    <img
      src="/images/learning-pond-no-line.webp"
      alt="A child sitting above a pond with a fishing rod, watching fish glide through the water."
      className="learning-pond__image block h-[27rem] w-full object-cover object-[47%_center] sm:h-[34rem]"
    />
    <div className="learning-pond__shade absolute inset-0" aria-hidden="true" />
    <svg className="learning-pond__line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <line x1="50.4" y1="0" x2="57.6" y2="61.5" />
    </svg>
    <div className="learning-pond__ripple learning-pond__ripple--one" aria-hidden="true" />
    <div className="learning-pond__ripple learning-pond__ripple--two" aria-hidden="true" />
    <div className="learning-pond__ripple learning-pond__ripple--three" aria-hidden="true" />
    <div className="learning-pond__catches" aria-hidden="true">
      {bloomCatches.map((catchItem, index) => (
        <div
          key={catchItem.name}
          className="learning-pond__catch"
          style={{
            '--catch-delay': `${index * 12}s`,
            '--fish-scale': catchItem.scale
          } as React.CSSProperties}
        >
          <Fish className="learning-pond__fish" strokeWidth={2.4} />
          <span className="learning-pond__fish-name">{catchItem.name}</span>
        </div>
      ))}
    </div>
    <figcaption className="absolute inset-x-0 bottom-0 z-10 p-6 text-left sm:p-10">
      <div className="max-w-xl">
        <h2 id="learning-pond-title" className="max-w-lg text-3xl font-bold tracking-[-0.03em] text-white sm:text-5xl">
          Your learning journey, one discovery at a time.
        </h2>
        <p className="mt-4 max-w-md text-base leading-relaxed text-blue-50 sm:text-lg">
          Set your path, meet the right tutor, and keep finding the next thing you can do.
        </p>
      </div>
    </figcaption>
  </figure>
  );
};

const HowItWorks = () => {
  const heroSectionRef = useRef<HTMLElement>(null);
  const featuresSectionRef = useRef<HTMLElement>(null);
  const tutorSectionRef = useRef<HTMLElement>(null);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  const [tutorsVisible, setTutorsVisible] = useState(false);
  const { localizedPlans } = useCountryPricing();

  useEffect(() => {
    const featureSection = featuresSectionRef.current;
    if (!featureSection) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setFeaturesVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.14 });
    observer.observe(featureSection);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const tutorSection = tutorSectionRef.current;
    if (!tutorSection) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTutorsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.18 });
    observer.observe(tutorSection);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="py-12">
        {/* Hero Section */}
        <section ref={heroSectionRef} className="relative py-20 px-4 text-center overflow-hidden">
          <AnimatedFavicon containerRef={heroSectionRef} />

        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            Personalized Learning Platform
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            How <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">rfLearn</span> Works
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Experience personalized learning with expert tutors, structured curriculam, and AI-powered progress tracking.
            Join thousands of students achieving their academic goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <Link to="/register" className="flex items-center gap-2">
                Start Learning Today <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
{/*             <Button variant="outline" size="lg">
              <Link to="/tutor/search" className="flex items-center gap-2">
                <PlayCircle className="w-5 h-5" />
                Watch Demo
              </Link>
            </Button> */}
          </div>
        </div>
      </section>

      {/* For Students Section */}
      <section ref={featuresSectionRef} className={`learning-flow py-20 px-4 bg-white ${featuresVisible ? 'is-visible' : ''}`} aria-labelledby="learning-flow-title">
        <div className="max-w-6xl mx-auto">
          <LearningPond />

          <div className="learning-flow__intro">
            <h2 id="learning-flow-title">Everything you need to keep learning moving.</h2>
            <span>One calm, connected space for planning sessions, working with your tutor, and seeing how far you’ve come.</span>
          </div>

          <ol className="learning-flow__track">
            {studentFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <li key={feature.title} className={`learning-flow__station learning-flow__station--${feature.accent}`} style={{ '--station-delay': `${index * 90}ms` } as React.CSSProperties}>
                  <div className="learning-flow__station-head">
                    <span className="learning-flow__marker" aria-hidden="true"><Icon size={21} strokeWidth={2.25} /></span>
                    <span className="learning-flow__count" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  <ul aria-label={`${feature.title} includes`}>
                    {feature.features.map((item) => (
                      <li key={item}><CheckCircle size={15} strokeWidth={2.4} aria-hidden="true" />{item}</li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Subscription Packages Preview */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Learning Path</h2>
            <p className="text-xl text-gray-600">Flexible packages designed for every learning goal</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {localizedPlans.map((plan) => {
              const IconComponent = iconMap[plan.icon as keyof typeof iconMap];
              return (
                <Card
                  key={plan.id}
                  className={`relative overflow-hidden ${
                    plan.popular ? 'ring-2 ring-green-500' : ''
                  } hover:shadow-xl transition-all duration-300`}
                >
                  {plan.popular && (
                    <div className="absolute top-0 left-0 w-full bg-green-500 text-white text-center py-2 text-sm font-medium flex items-center justify-center gap-1">
                      <Sparkles className="w-4 h-4" />
                      Most Popular
                    </div>
                  )}

                  <CardHeader className={`text-center ${plan.popular ? 'pt-12' : 'pt-6'} pb-4`}>
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center mx-auto mb-3`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <p className="text-gray-600 text-sm">{plan.description}</p>

                    {/* Compact Pricing Display */}
                    <div className="mt-3">
                      <div className="flex items-center justify-center space-x-2">
                        {plan.formattedOriginalPrice && (
                          <span className="text-sm text-gray-400 line-through">
                            {plan.formattedOriginalPrice}
                          </span>
                        )}
                        <span className="text-2xl font-bold text-gray-900">
                          {plan.formattedPrice}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {plan.priceType === 'per_session' ? '/session' : plan.duration}
                      </p>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    {/* Key Features - Limited to 4 for compact display */}
                    <ul className="space-y-2">
                      {plan.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                      {plan.features.length > 4 && (
                        <li className="text-xs text-gray-500 text-center pt-1">
                          +{plan.features.length - 4} more features
                        </li>
                      )}
                    </ul>

                    {/* Sessions Info */}
                    <div className="flex items-center justify-center space-x-2 mt-4 text-xs text-gray-600 bg-gray-50 rounded-lg py-2">
                      <Clock className="w-3 h-3" />
                      <span>{plan.sessions}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* View Full Pricing Link */}
          <div className="text-center mt-8">
            <Button variant="outline" asChild>
              <Link to="/pricing" className="flex items-center gap-2">
                View Detailed Pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* For Tutors Section */}
      <section ref={tutorSectionRef} className={`tutor-constellation py-20 px-4 ${tutorsVisible ? 'is-visible' : ''}`} aria-labelledby="tutor-benefits-title">
        <div className="max-w-6xl mx-auto">
          <div className="tutor-constellation__intro">
            <h2 id="tutor-benefits-title">Empower students. Build a career with reach.</h2>
            <p>
              Join our community of expert tutors and make a meaningful impact while building your teaching career
            </p>
          </div>

          <div className="tutor-constellation__benefits">
            {tutorBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <article key={benefit.title} className="tutor-constellation__benefit" style={{ '--benefit-delay': `${index * 95}ms` } as React.CSSProperties}>
                  <div className="tutor-constellation__benefit-top">
                    <span className="tutor-constellation__icon" aria-hidden="true"><Icon size={22} strokeWidth={2.25} /></span>
                    <span>{benefit.highlight}</span>
                  </div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </article>
              );
            })}
          </div>

          {/* Tutor Journey */}
          <div className="tutor-journey mt-16">
            <h3>Your Journey as a Tutor</h3>
            <ol>
              {[
                {
                  step: "Apply",
                  description: "Submit your application with credentials and expertise",
                  icon: <FileText className="w-6 h-6" />
                },
                {
                  step: "Verify",
                  description: "Complete our verification process and background check",
                  icon: <Shield className="w-6 h-6" />
                },
                {
                  step: "Onboard",
                  description: "Complete training and set up your tutor profile",
                  icon: <UserCheck className="w-6 h-6" />
                },
                {
                  step: "Teach",
                  description: "Start teaching and building your reputation",
                  icon: <GraduationCap className="w-6 h-6" />
                }
              ].map((step, index) => (
                <li key={step.step} style={{ '--journey-delay': `${index * 110}ms` } as React.CSSProperties}>
                  <span className="tutor-journey__marker" aria-hidden="true">{step.icon}</span>
                  <span className="tutor-journey__number" aria-hidden="true">{index + 1}</span>
                  <h4>{step.step}</h4>
                  <p>{step.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-xl text-gray-600">Real results from real students and tutors</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                type: "student",
                name: "Melissa",
                role: "Pre-Calculus Student",
                quote: "My tutor was very helpful and explained the math problem to me step by step. I felt more confident in solving more problems like these after our session",
                rating: 5,
                achievement: "Improved grades by 40%"
              },
              {
                type: "tutor",
                name: "Faran",
                role: "Mathematics Tutor",
                quote: "Teaching on rf has been incredibly rewarding. The platform's tools help me track student progress and adapt my teaching methods effectively.",
                rating: 5,
                achievement: "500+ successful sessions"
              },
              {
                type: "student",
                name: "Alex",
                role: "High School Student",
                quote: "Faran helped me through the problem and made me also repeat it back to ensure I understood.",
                rating: 4,
                achievement: "Gained confidence in the topic"
              }
            ].map((story, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                      {story.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h4 className="font-semibold">{story.name}</h4>
                      <p className="text-sm text-gray-600">{story.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(story.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4 italic">"{story.quote}"</p>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    {story.achievement}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Learning?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of students and tutors who are already experiencing the future of education
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100">
              <Link to="/register" className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                Start as Student
              </Link>
            </Button>
            <Button size="lg" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-colors">
              <Link to="/become-tutor" className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Become a Tutor
              </Link>
            </Button>
          </div>
        </div>
      </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowItWorks;
